/**
 * Copy an image (by URL) to the OS clipboard.
 *
 * iOS Safari is the strict one here: it requires that the call to
 * `navigator.clipboard.write` be made SYNCHRONOUSLY inside the user gesture
 * handler. Any `await` before that call breaks "user activation" and the
 * call silently fails with NotAllowedError.
 *
 * The trick: construct `ClipboardItem` with an INNER promise that resolves
 * to the blob. iOS treats the synchronous `ClipboardItem({ "image/png":
 * Promise<Blob> })` + `clipboard.write([item])` as still-within-gesture
 * even though the blob is fetched/encoded asynchronously inside.
 *
 * Returns true on success, false otherwise (caller shows toast / fallback).
 *
 * Source: WebKit blog "Clipboard API improvements" + MDN clipboard docs.
 */
export async function copyImageToClipboard(url) {
  if (!url || typeof navigator === "undefined" || !navigator.clipboard || !window.ClipboardItem) {
    return false;
  }

  // Build the blob lazily inside the ClipboardItem so we DON'T await before
  // calling clipboard.write — required for iOS Safari user-activation.
  const blobPromise = (async () => {
    const resp = await fetch(url, { credentials: "omit", mode: "cors" });
    if (!resp.ok) throw new Error(`fetch ${resp.status}`);
    const srcBlob = await resp.blob();

    // iOS Safari only accepts a small set of MIME types in the clipboard;
    // image/png is the safest choice.  If the original is already PNG we
    // can reuse it directly; otherwise re-encode via canvas.
    if (srcBlob.type === "image/png") return srcBlob;

    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
      };
      img.onerror = () => reject(new Error("image load failed"));
      img.src = URL.createObjectURL(srcBlob);
    });
  })();

  try {
    // ⚠️ Do NOT await blobPromise before this line — pass it inside the
    // ClipboardItem so iOS keeps the user-activation alive through the
    // async fetch + canvas re-encode.
    await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blobPromise })]);
    return true;
  } catch (err) {
    // Best-effort fallback: try writing the resolved blob synchronously.
    try {
      const blob = await blobPromise;
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * iOS / Android share-sheet for an image. When the OS clipboard refuses to
 * accept image data (older iOS, Firefox Android), this opens the native
 * share UI with the file already attached. The user picks WhatsApp →
 * Paperless contact → message is sent with the real photo.
 *
 * Returns true if the share dialog was successfully shown (regardless of
 * whether the user completed the share).
 */
export async function shareImageNative(url, filename = "receipt.jpg") {
  if (!url || typeof navigator === "undefined" || !navigator.share) return false;
  try {
    const resp = await fetch(url, { credentials: "omit", mode: "cors" });
    if (!resp.ok) return false;
    const blob = await resp.blob();
    const file = new File([blob], filename, { type: blob.type || "image/jpeg" });
    const data = { files: [file], title: filename };
    if (navigator.canShare && !navigator.canShare(data)) return false;
    await navigator.share(data);
    return true;
  } catch {
    return false;
  }
}

/** Crude UA sniff — only used to tweak the default behaviour on iOS. */
export const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
};
